import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/common/Loading";

// Otter Assist admin — aggregate analytics (OAD-125).
// Qualified and Strong are reported SIDE BY SIDE, each labelled with the CURRENT threshold
// supplied by the backend (Qualified >= 35 since OAD-141, Strong >= 60). Never hardcode a number.
// No named member, helper note, candidate score, contact detail or vector can appear here.

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const num = (v) => (v === null || v === undefined ? "—" : String(v));

function Group({ title, testId, children }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="text-sm font-[640]">{title}</h3>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">{children}</dl>
    </Card>
  );
}

function Metric({ label, value, testId }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-[600] tabular-nums" data-testid={testId}>{value}</dd>
    </>
  );
}

export function OaAnalytics() {
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["oa-admin-analytics", from, to],
    queryFn: async () => (await api.get("/admin/otter-assist/analytics", {
      params: { date_from: from || undefined, date_to: to || undefined },
    })).data,
  });

  if (isLoading) return <Loading />;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="oa-admin-analytics">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
          From
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="mt-1 h-9 w-[160px] text-[13px]" data-testid="oa-analytics-from" />
        </label>
        <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
          To
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="mt-1 h-9 w-[160px] text-[13px]" data-testid="oa-analytics-to" />
        </label>
        <p className="text-[11px] text-muted-foreground">
          Aggregate only. Scoring {data.thresholds?.scoring_version}.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Group title="Assists" testId="oa-analytics-assists">
          <Metric label="Open for help now" value={num(data.assists?.active_now)}
            testId="oa-analytics-active" />
          <Metric label="Activated" value={num(data.assists?.activated)} />
          <Metric label="Help received" value={num(data.assists?.help_received)} />
          <Metric label="Help received rate" value={pct(data.assists?.help_received_rate)}
            testId="oa-analytics-help-rate" />
          <Metric label="Closed" value={num(data.assists?.closed)} />
          <Metric label="Expired" value={num(data.assists?.expired)} />
          <Metric label="Admin cancelled" value={num(data.assists?.admin_cancelled)} />
        </Group>

        <Group title="People offering help" testId="oa-analytics-help">
          <Metric label="People offering help" value={num(data.help?.people_offering_help)}
            testId="oa-analytics-helpers" />
          <Metric label="Offers" value={num(data.help?.offers)} />
          <Metric label="I Can Help conversion" value={pct(data.help?.i_can_help_conversion)} />
          <Metric label="Found the post themselves" value={num(data.help?.public_discovery_offers)} />
          <Metric label="Public discovery share" value={pct(data.help?.public_discovery_share)} />
          <Metric label="Withdrawal rate" value={pct(data.help?.withdrawal_rate)} />
          <Metric label="Not a good fit" value={num(data.help?.not_a_good_fit)} />
          <Metric label="Not right now" value={num(data.help?.not_right_now)} />
        </Group>

        <Group title="Matching quality" testId="oa-analytics-matching">
          <Metric label="Runs completed" value={num(data.matching?.runs_completed)} />
          <Metric label="Runs failed" value={num(data.matching?.runs_failed)}
            testId="oa-analytics-runs-failed" />
          <Metric label="Failure rate" value={pct(data.matching?.failure_rate)} />
          <Metric label="Assists in matching failure"
            value={num(data.matching?.assists_matching_failed)}
            testId="oa-analytics-assists-matching-failed" />
          <Metric label="No matches" value={num(data.matching?.no_matches)} />
          <Metric label={`Avg ${data.matching?.qualified_label} per Assist`}
            value={num(data.matching?.avg_qualified_matches_per_assist)} />
          <Metric label={`% with < 5 ${data.matching?.qualified_label}`}
            value={pct(data.matching?.pct_under_5_qualified)}
            testId="oa-analytics-under5-qualified" />
          <Metric label={`% with < 5 ${data.matching?.strong_label}`}
            value={pct(data.matching?.pct_under_5_strong)}
            testId="oa-analytics-under5-strong" />
        </Group>

        <Group title="Delivery" testId="oa-analytics-delivery">
          <Metric label="Matches delivered" value={num(data.delivery?.delivered)}
            testId="oa-analytics-delivered" />
          <Metric label="Failed" value={num(data.delivery?.failed)} />
          <Metric label="Failure rate" value={pct(data.delivery?.failure_rate)} />
          <Metric label="Queue backlog" value={num(data.delivery?.queue_backlog)} />
        </Group>

        <Group title="Email digests" testId="oa-analytics-digests">
          <Metric label="Sent" value={num(data.digests?.sent)} />
          <Metric label="Empty" value={num(data.digests?.empty)} />
          <Metric label="Failed" value={num(data.digests?.failed)} />
          <Metric label="Needs emailed" value={num(data.digests?.items_emailed)} />
          <dt className="col-span-2 mt-1 text-[11px] italic text-muted-foreground"
            data-testid="oa-analytics-engagement-unavailable">
            Digest engagement: unavailable — no open/click instrumentation exists, so it is not
            inferred.
          </dt>
        </Group>

        <Group title="Notifications & preferences" testId="oa-analytics-notify">
          <Metric label="Notifications sent" value={num(data.notifications?.sent)} />
          <Metric label="Notifications failed" value={num(data.notifications?.failed)} />
          <Metric label="Members with preferences"
            value={num(data.preferences?.members_with_preferences)} />
          <Metric label="Digests turned off" value={num(data.preferences?.digests_off)} />
          <Metric label="Opt-out rate" value={pct(data.preferences?.opt_out_rate)}
            testId="oa-analytics-optout" />
        </Group>

        <Group title="Moderation" testId="oa-analytics-moderation">
          <Metric label="Reports" value={num(data.moderation?.reports)} />
          <Metric label="Admin actions" value={num(data.moderation?.admin_actions)} />
        </Group>
      </div>

      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <BarChart3 className="h-3.5 w-3.5" />
        Aggregate figures only — no member names, helper messages, contact details or match internals
        appear on this page.
      </p>
    </div>
  );
}

export default OaAnalytics;
